import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Megaphone, Send, X, History, ChevronDown, ChevronRight, CheckCircle, Volume2, VolumeX, Save } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/utils/api';
import { hasRole } from '@/utils/auth';
import { toast } from 'sonner';

const CATEGORY_STYLE = {
  task_reminders: 'bg-blue-500', signal_reminders: 'bg-amber-500', weekly_digest: 'bg-emerald-500',
  security_alerts: 'bg-red-500', announcements: 'bg-indigo-500',
};
const FILTERS = [
  { key: 'all', label: 'All', cats: null },
  { key: 'tasks', label: 'Tasks', cats: ['task_reminders'] },
  { key: 'signals', label: 'Signals', cats: ['signal_reminders', 'weekly_digest'] },
  { key: 'security', label: 'Security', cats: ['security_alerts'] },
  { key: 'announcements', label: 'News', cats: ['announcements'] },
];
const HIGH_PRIORITY = new Set(['security_alerts', 'announcements']);
const ROLE_OPTIONS = ['WORKER', 'SUPERVISOR', 'ENGINEER', 'MANAGER', 'EXECUTIVE'];

function relativeTime(iso) {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine'; o.frequency.value = 880;
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    o.start(); o.stop(ctx.currentTime + 0.35);
  } catch { /* audio not available */ }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [expandedDigest, setExpandedDigest] = useState({});
  const [chimeEnabled, setChimeEnabled] = useState(() => localStorage.getItem('safer_chime') !== 'off');
  // composer
  const [composing, setComposing] = useState(false);
  const [aTitle, setATitle] = useState('');
  const [aBody, setABody] = useState('');
  const [aTarget, setATarget] = useState('org');
  const [aRole, setARole] = useState('any');
  const [aRequireAck, setARequireAck] = useState(false);
  const [targetOptions, setTargetOptions] = useState([{ value: 'org', label: 'Everyone in the org' }]);
  const [audiences, setAudiences] = useState([]);
  const [sending, setSending] = useState(false);
  // history
  const [historyView, setHistoryView] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [ackExpanded, setAckExpanded] = useState({});
  const [recipients, setRecipients] = useState({});

  const navigate = useNavigate();
  const canAnnounce = hasRole(['HQ', 'ADMIN', 'EXECUTIVE', 'DIRECTOR']);
  const prevUnread = useRef(null);
  const chimeRef = useRef(chimeEnabled);
  useEffect(() => { chimeRef.current = chimeEnabled; }, [chimeEnabled]);

  const fetchUnread = useCallback(async () => {
    try {
      const r = await api.get('/notifications/unread-count');
      const c = r.data.count || 0;
      setUnread(c);
      if (prevUnread.current !== null && c > prevUnread.current) {
        try {
          const lr = await api.get('/notifications?limit=1');
          const latest = (lr.data.notifications || [])[0];
          if (latest) {
            toast(latest.title, { description: latest.body });
            if (chimeRef.current && HIGH_PRIORITY.has(latest.category)) playChime();
          }
        } catch { /* noop */ }
      }
      prevUnread.current = c;
    } catch { /* noop */ }
  }, []);

  const fetchList = useCallback(() => {
    setLoading(true);
    api.get('/notifications?limit=30')
      .then((r) => { setItems(r.data.notifications || []); setUnread(r.data.unread_count || 0); prevUnread.current = r.data.unread_count || 0; })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fetchHistory = useCallback(() => {
    setLoadingHistory(true);
    api.get('/notifications/announcements').then((r) => setAnnouncements(r.data.announcements || [])).catch(() => {}).finally(() => setLoadingHistory(false));
  }, []);

  const loadComposerData = useCallback(() => {
    api.get('/org-structure').then((r) => {
      const opts = [{ value: 'org', label: 'Everyone in the org' }];
      (r.data.business_units || []).forEach((bu) => {
        opts.push({ value: `business_unit:${bu.bu_id}`, label: `Unit: ${bu.name}` });
        (bu.departments || []).forEach((d) => (d.sites || []).forEach((s) => opts.push({ value: `site:${s.site_id}`, label: `Site: ${s.name}` })));
      });
      setTargetOptions(opts);
    }).catch(() => {});
    api.get('/notifications/audiences').then((r) => setAudiences(r.data.audiences || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const tick = () => { if (!document.hidden) fetchUnread(); };
    tick();
    const t = setInterval(tick, 30000);
    const onVis = () => { if (!document.hidden) fetchUnread(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchUnread]);

  useEffect(() => { if (open && !historyView) fetchList(); }, [open, historyView, fetchList]);

  const toggleChime = () => setChimeEnabled((c) => { localStorage.setItem('safer_chime', c ? 'off' : 'on'); return !c; });

  const handleItemClick = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.notification_id}/read`); } catch { /* noop */ }
      setItems((p) => p.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.data?.url) { setOpen(false); navigate(n.data.url); }
  };

  const acknowledge = async (n) => {
    try {
      await api.post(`/notifications/${n.notification_id}/acknowledge`);
      setItems((p) => p.map((x) => x.notification_id === n.notification_id ? { ...x, acknowledged: true, read: true } : x));
      toast.success('Acknowledged');
    } catch { /* noop */ }
  };

  const markAllRead = async () => {
    try { await api.post('/notifications/read-all'); setItems((p) => p.map((x) => ({ ...x, read: true }))); setUnread(0); } catch { /* noop */ }
  };

  const openComposer = () => setComposing((c) => { const next = !c; if (next && targetOptions.length <= 1) loadComposerData(); return next; });

  const applyAudience = (audId) => {
    const a = audiences.find((x) => x.audience_id === audId);
    if (!a) return;
    setATarget(a.target_type === 'org' ? 'org' : `${a.target_type}:${a.target_id}`);
    setARole(a.target_role || 'any');
  };

  const currentTargetLabel = () => {
    const base = targetOptions.find((o) => o.value === aTarget)?.label || 'Everyone';
    return aRole !== 'any' ? `${base} · ${aRole}` : base;
  };

  const sendAnnouncement = async () => {
    if (!aTitle.trim() || !aBody.trim()) { toast.error('Add a title and message'); return; }
    const [ttype, tid] = aTarget.includes(':') ? aTarget.split(':') : ['org', null];
    setSending(true);
    try {
      const r = await api.post('/notifications/announce', {
        title: aTitle.trim(), body: aBody.trim(), url: '/dashboard',
        target_type: ttype, target_id: tid, target_role: aRole === 'any' ? null : aRole,
        target_label: currentTargetLabel(), require_ack: aRequireAck,
      });
      toast.success(`Sent to ${r.data.recipients} member${r.data.recipients !== 1 ? 's' : ''}`);
      setATitle(''); setABody(''); setATarget('org'); setARole('any'); setARequireAck(false); setComposing(false);
      fetchList();
    } catch { /* noop */ } finally { setSending(false); }
  };

  const saveAudience = async () => {
    const name = window.prompt('Name this audience (e.g. "Rig Alpha supervisors")');
    if (!name || !name.trim()) return;
    const [ttype, tid] = aTarget.includes(':') ? aTarget.split(':') : ['org', null];
    try {
      await api.post('/notifications/audiences', {
        name: name.trim(), target_type: ttype, target_id: tid,
        target_role: aRole === 'any' ? null : aRole, target_label: currentTargetLabel(),
      });
      toast.success('Audience saved');
      api.get('/notifications/audiences').then((r) => setAudiences(r.data.audiences || [])).catch(() => {});
    } catch { /* noop */ }
  };

  const toggleAckList = async (aid) => {
    const next = !ackExpanded[aid];
    setAckExpanded((s) => ({ ...s, [aid]: next }));
    if (next && !recipients[aid]) {
      try {
        const r = await api.get(`/notifications/announcements/${aid}/recipients`);
        setRecipients((s) => ({ ...s, [aid]: r.data.recipients || [] }));
      } catch { /* noop */ }
    }
  };

  const activeCats = FILTERS.find((f) => f.key === filter)?.cats;
  const filteredItems = activeCats ? items.filter((n) => activeCats.includes(n.category)) : items;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-muted transition-colors" data-testid="notification-bell-button" aria-label="Notifications">
          <Bell className="w-5 h-5 text-muted-foreground" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-unread-badge">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[390px] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm">{historyView ? 'Announcement history' : 'Notifications'}</span>
          <div className="flex items-center gap-1">
            <button onClick={toggleChime} className="p-1 rounded hover:bg-muted text-muted-foreground" title={chimeEnabled ? 'Sound on' : 'Sound off'} data-testid="notification-chime-toggle">
              {chimeEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
            </button>
            {canAnnounce && (
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" data-testid="notification-history-toggle"
                onClick={() => { const next = !historyView; setHistoryView(next); if (next) fetchHistory(); }}>
                <History className="w-3.5 h-3.5 mr-1" /> {historyView ? 'Back' : 'History'}
              </Button>
            )}
            {!historyView && canAnnounce && (
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" data-testid="notification-announce-toggle" onClick={openComposer}>
                <Megaphone className="w-3.5 h-3.5 mr-1" /> Announce
              </Button>
            )}
            {!historyView && unread > 0 && (
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" data-testid="notification-mark-all-read" onClick={markAllRead}>
                <CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark all
              </Button>
            )}
          </div>
        </div>

        {historyView ? (
          <ScrollArea className="max-h-[440px]" data-testid="announcement-history-list">
            {loadingHistory ? (<p className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</p>)
              : announcements.length === 0 ? (
                <div className="px-4 py-10 text-center"><Megaphone className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" /><p className="text-sm text-muted-foreground">No announcements sent yet</p></div>
              ) : (
                <div>
                  {announcements.map((a) => (
                    <div key={a.announcement_id} className="px-4 py-3 border-b last:border-0" data-testid={`announcement-history-${a.announcement_id}`}>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium flex-1">{a.title}</p>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{a.target_label || 'Everyone'}</span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{a.body}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10px] text-muted-foreground/70">by {a.sent_by_name} · {relativeTime(a.sent_at)}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-medium text-indigo-600" data-testid={`announcement-readcount-${a.announcement_id}`}>{a.read_count} of {a.recipient_count} read</span>
                          {a.require_ack && (
                            <button onClick={() => toggleAckList(a.announcement_id)} className="text-[10px] font-medium text-emerald-600 flex items-center gap-0.5" data-testid={`announcement-ack-toggle-${a.announcement_id}`}>
                              {(a.ack_count ?? 0)} of {a.recipient_count} ack'd {ackExpanded[a.announcement_id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            </button>
                          )}
                        </div>
                      </div>
                      {a.require_ack && ackExpanded[a.announcement_id] && (
                        <ul className="mt-2 pl-1 space-y-1" data-testid={`announcement-recipients-${a.announcement_id}`}>
                          {(recipients[a.announcement_id] || []).map((rec) => (
                            <li key={rec.user_id} className="text-xs flex items-center gap-2">
                              {rec.acknowledged ? <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" /> : <span className="w-3 h-3 rounded-full border border-muted-foreground/40 shrink-0" />}
                              <span className={rec.acknowledged ? '' : 'text-muted-foreground'}>{rec.full_name}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </ScrollArea>
        ) : (
          <>
            {composing && (
              <div className="p-3 border-b bg-muted/30 space-y-2" data-testid="notification-announce-composer">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">New announcement</span>
                  <button onClick={() => setComposing(false)} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
                </div>
                {audiences.length > 0 && (
                  <Select onValueChange={applyAudience}>
                    <SelectTrigger className="h-8 text-xs" data-testid="announce-audience-select"><SelectValue placeholder="Use a saved audience…" /></SelectTrigger>
                    <SelectContent>{audiences.map((a) => (<SelectItem key={a.audience_id} value={a.audience_id} className="text-xs">{a.name}</SelectItem>))}</SelectContent>
                  </Select>
                )}
                <Input placeholder="Title" value={aTitle} onChange={(e) => setATitle(e.target.value)} className="h-8 text-sm" data-testid="announce-title-input" />
                <Textarea placeholder="Message…" value={aBody} onChange={(e) => setABody(e.target.value)} rows={2} className="text-sm" data-testid="announce-body-input" />
                <div className="flex gap-2">
                  <Select value={aTarget} onValueChange={setATarget}>
                    <SelectTrigger className="h-8 text-xs flex-1" data-testid="announce-target-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{targetOptions.map((o) => (<SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>))}</SelectContent>
                  </Select>
                  <Select value={aRole} onValueChange={setARole}>
                    <SelectTrigger className="h-8 text-xs w-[130px]" data-testid="announce-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any" className="text-xs">Any role</SelectItem>
                      {ROLE_OPTIONS.map((rl) => (<SelectItem key={rl} value={rl} className="text-xs">{rl}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox checked={aRequireAck} onCheckedChange={(c) => setARequireAck(!!c)} data-testid="announce-require-ack" />
                  Require acknowledgement
                </label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={saveAudience} data-testid="announce-save-audience"><Save className="w-3.5 h-3.5 mr-1" /> Save audience</Button>
                  <Button size="sm" className="flex-1 h-8 text-xs" disabled={sending} onClick={sendAnnouncement} data-testid="announce-send-button"><Send className="w-3.5 h-3.5 mr-1" /> {sending ? 'Sending…' : 'Send'}</Button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-1 px-2 py-2 border-b overflow-x-auto" data-testid="notification-filters">
              {FILTERS.map((f) => (
                <button key={f.key} onClick={() => setFilter(f.key)}
                  className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${filter === f.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
                  data-testid={`notification-filter-${f.key}`}>{f.label}</button>
              ))}
            </div>

            <ScrollArea className="max-h-[360px]">
              {loading ? (<p className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</p>)
                : filteredItems.length === 0 ? (
                  <div className="px-4 py-10 text-center" data-testid="notification-empty"><Bell className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" /><p className="text-sm text-muted-foreground">{filter === 'all' ? "You're all caught up" : 'Nothing here'}</p></div>
                ) : (
                  <div>
                    {filteredItems.map((n) => {
                      const isDigest = n.data?.type === 'daily_digest' && Array.isArray(n.data?.digest_items);
                      const isOpen = !!expandedDigest[n.notification_id];
                      const needsAck = n.data?.require_ack && !n.acknowledged;
                      return (
                        <div key={n.notification_id} className={`border-b last:border-0 ${n.read && !needsAck ? 'opacity-60' : ''}`} data-testid={`notification-item-${n.notification_id}`}>
                          <button onClick={() => handleItemClick(n)} className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors flex gap-3">
                            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? 'bg-transparent' : (CATEGORY_STYLE[n.category] || 'bg-primary')}`} />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">{n.title}</p>
                              <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                              <p className="text-[10px] text-muted-foreground/70 mt-1">{relativeTime(n.created_at)}</p>
                            </div>
                          </button>
                          {needsAck && (
                            <div className="px-4 pb-2">
                              <Button size="sm" className="h-7 text-xs" onClick={() => acknowledge(n)} data-testid={`notification-ack-${n.notification_id}`}>
                                <CheckCircle className="w-3.5 h-3.5 mr-1" /> Acknowledge
                              </Button>
                            </div>
                          )}
                          {n.data?.require_ack && n.acknowledged && (
                            <p className="px-4 pb-2 text-[11px] text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Acknowledged</p>
                          )}
                          {isDigest && (
                            <div className="px-4 pb-2">
                              <button onClick={() => setExpandedDigest((s) => ({ ...s, [n.notification_id]: !s[n.notification_id] }))} className="flex items-center gap-1 text-[11px] text-primary hover:underline" data-testid={`digest-expand-${n.notification_id}`}>
                                {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} See what's inside ({n.data.digest_count || n.data.digest_items.length})
                              </button>
                              {isOpen && (
                                <ul className="mt-1.5 pl-4 space-y-1" data-testid={`digest-items-${n.notification_id}`}>
                                  {n.data.digest_items.map((it, i) => (
                                    <li key={i} className="text-xs text-muted-foreground flex gap-2"><span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${CATEGORY_STYLE[it.category] || 'bg-primary'}`} /><span className="truncate">{it.title}{it.body ? ` — ${it.body}` : ''}</span></li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
            </ScrollArea>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
